import { AlertTriangle, Home, RotateCw } from 'lucide-react'
import { useEffect } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router'
import { Button, LogoMark } from '@/components/ui'
import { reportError } from '@/lib/analytics'
import { useDocumentTitle } from '@/lib/hooks'
import { t } from '@/lib/i18n'
import { clearChunkReloadFlag, isChunkLoadError, RELOAD_FLAG, reloadPage } from '../chunk'
import NotFoundPage from './NotFoundPage'

/** Router errorElement: crashed pages, failed lazy chunks and thrown 404 responses. */
export default function RouteErrorPage() {
  const error = useRouteError()
  const chunk = isChunkLoadError(error)
  // E2E-J10-03: a chunk that failed while the phone is offline is the network, not a deploy. Reloading now only
  // swaps the app for the browser error page, so wait for the connection and say what is really going on.
  const offline = chunk && typeof navigator !== 'undefined' && navigator.onLine === false
  useDocumentTitle([t('Something went wrong')])

  useEffect(() => {
    if (offline) {
      const back = () => reloadPage()
      window.addEventListener('online', back, { once: true })
      return () => window.removeEventListener('online', back)
    }
    if (chunk) {
      // Reload once automatically after a deploy; avoid loops when the network is the problem.
      try {
        if (!sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1')
          reloadPage()
          return
        }
      } catch {
        /* storage blocked */
      }
    } else {
      reportError(error)
    }
  }, [chunk, error, offline])

  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />

  return (
    <div className="grid min-h-dvh place-items-center bg-page px-4 py-10 text-text">
      <div className="w-full max-w-[420px] rounded-modal border border-line bg-surface p-6 text-center sm:p-8">
        <div className="mx-auto mb-5 w-fit">
          <LogoMark height={40} />
        </div>
        <span className="mx-auto mb-4 grid size-12 place-items-center rounded-full bg-danger-tint text-danger" aria-hidden="true">
          <AlertTriangle size={22} strokeWidth={1.75} />
        </span>
        <h1 className="text-page-title">
          {offline ? t('You are offline') : chunk ? t('A new version is ready') : t('Something went wrong')}
        </h1>
        <p className="mt-2 text-body text-muted">
          {offline
            ? t('Check your connection. This page loads again by itself as soon as you are back online.')
            : chunk
              ? t('MG AI School was updated while this tab was open. Reload to continue.')
              : t('This page hit an unexpected error. Try again, or head back home.')}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button
            variant="primary"
            onClick={() => {
              clearChunkReloadFlag()
              reloadPage()
            }}
          >
            <RotateCw size={18} strokeWidth={1.75} aria-hidden="true" />
            {t('Reload')}
          </Button>
          <Button asChild variant="secondary">
            <a href="/">
              <Home size={18} strokeWidth={1.75} aria-hidden="true" />
              {t('Go home')}
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}

